import { css, cx } from '@linaria/core'
import { type FC, type HTMLProps, type ReactNode, useCallback, useRef, useState } from 'react'
import { Dropdown } from '#components/Dropdown'
import { Icon } from '#components/Icon'
import CheckmarkIcon from '#icons/check-24.svg'
import ChevronIcon from '#icons/chevron-1-down-24.svg'
import { fragments, style } from '#styles/index'
import { useIntersections } from '#utils/useIntersections'

const headerTabsClass = css`
  display: flex;
  flex: 1;
  height: 39px;
  flex-basis: auto;
  overflow: hidden;

  > ._tabs-container {
    position: relative;
    width: 100%;
    height: 100%;
    flex: 1;
    flex-basis: auto;
    overflow: hidden;

    > ._tabs-rail {
      position: absolute;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: ${style.themeColors.backgroundDefault};
      // this is the bottom line, it serves the purpose to *not* reserve space
      box-shadow: inset 0 -2px 0 ${style.themeColors.lineDefault};    

      flex: 1;
      display: flex;
      flex-direction: row;
      flex-basis: auto;
      overflow: hidden;
      transition: 0.25s ease opacity;

      &._visible {
        opacity: 1;
      }

      &._invisible {
        opacity: 0;
        pointer-events: none;
      }

      > ._tabs {
        height: 100%;
        flex: 1;
        display: flex;
        flex-direction: row;

        width: fit-content;
      }
    }
  }
`

const tabClass = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  max-width: 230px;
  color: ${style.themeColors.textDefault};
  ${fragments.textStyle.tab.m};
  transition: ease 0.25s border-bottom-color, ease 0.25s color;
  border-bottom: 2px solid ${style.themeColors.lineDefault};

  &:hover {
    border-bottom-color: ${style.themeColors.textDisabled};
  }

  &._highlighted {
    border-bottom-color: ${style.themeColors.lineInput};
  }

  &._selected {
    color:${style.themeColors.textImportant};
    border-bottom-color: ${style.themeColors.lineImportant};
  }

  > ._chevron {
    cursor: pointer;
    color: ${style.themeColors.textImportant};

    
    margin-right: 4px;
    transition: ease 0.25s transform;  

    &._open {
      transform: rotateX(180deg);
      color: ${style.themeColors.textImportant};
    }
  }

  > ._entry, > ._dropdown-anchor > ._entry {
    cursor: pointer;
    height: 100%;
    padding: 0 4px 0 8px;

    &._with-chevron {
      padding-right: 0;
    }

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    
    max-width: 230px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    user-select: none;
    
    color: currentColor;
    ${fragments.textStyle.tab.m};
    
    transition: ease 0.25s color;
  }
  
  &._selected > ._entry {
    color:${style.themeColors.textImportant};
  }

  &._pending > ._entry {
    cursor: progress;
    color: ${style.themeColors.textDimmed};
  }
`

const dropdownClass = css`
  display: flex;
  flex-direction: column;
  padding: 8px 0px;

  > ._dropdown-entry {
    cursor: pointer;
    transition: ease 0.15s background-color;

    &._selected {
      background-color: ${style.themeColors.backgroundBoxed};
      > ._entry > ._icon {
        color: ${style.themeColors.textImportant}
      }
    }
    
    &:hover {
      background-color: ${style.themeColors.backgroundBoxedHover};
    }

    > ._entry {
      user-select: none;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 12px;
     
      gap: 8px;
      ${fragments.textStyle.body.m.regular};

      > ._icon {
        color: transparent;
      }
    }

    &._pending {
      cursor: progress;
      background-color: ${style.themeColors.backgroundDefault};
      > ._entry {
        color: ${style.themeColors.textDimmed};
      }
    }

  }
`

export const HeaderDropdownEntry: FC<HTMLProps<HTMLDivElement> & { selected?: boolean }> = ({ children, className, selected, ...rest }) => {
  return (
    <div className={cx(className, '_entry', selected && '_selected')} {...rest}>
      {children}
      <Icon className="_icon" iconUrl={CheckmarkIcon} />
    </div>
  )
}

export const HeaderTabEntry: FC<HTMLProps<HTMLDivElement>> = ({ children, className, selected, ...rest }) => (
  <div {...rest} className={cx('_entry', className)}>
    {children}
  </div>
)

export type HeaderTabEntryDefinition = {
  key: string
  pending?: boolean
  /** Disables the selection of this item */
  cannotBeSelected?: boolean
  /** Puts a dark bar beneath the header element, if not directly selected */
  highlighted?: boolean
  tab: ReactNode
  /**
   * The component is wrapped in a div, which can be controlled.
   * There is a class `_selected` attached, if the tab is the current selection.
   * This allows you to write `&._selected { ... }` css
   */
  tabWrappingClass?: string
  dropdown: ReactNode
  /**
   * The dropdown is wrapped in a div, which can be controlled
   * There is a class `_selected` attached, if the entry is the current selection.
   * This allows you to write `&._selected { ... }` css
   */
  dropdownWrappingClass?: string
  alwaysVisible?: boolean
}

/**
 * Header Tabs for displaying a solution for collapsing tabs.
 * Best paired with {@link HeaderDropdownEntry} and {@link HeaderTabEntry}.
 */
export const HeaderTabs: FC<
  {
    className?: string
    entries: HeaderTabEntryDefinition[]
  } & ({ selection?: string; setSelection?: (s: string) => unknown } | undefined)
> = ({ className, entries, selection: passedSelection, setSelection: setSelectionCallback }) => {
  const { addRef, getEntries } = useIntersections(undefined, { threshold: [1] })
  const allVisible = getEntries().every((x) => !x.lastEntry || x.lastEntry.intersectionRatio >= 1)
  const [open, setOpen] = useState(false)

  const [storedSelection, setStoredSelection] = useState<string>()
  const selection = passedSelection ?? storedSelection
  const currentSelection = entries.find((x) => x.key === selection)
  const firstEntry: (typeof entries)[number] | undefined = currentSelection ?? entries[0]
  const alwaysVisible = entries.filter((x) => x.alwaysVisible && x !== firstEntry)
  const chevronRef = useRef<HTMLDivElement>(null)
  const setSelection = useCallback(
    (selection: string) => {
      const entry = entries.find((x) => x.key === selection)
      if (!entry || entry.pending || entry.cannotBeSelected) {
        return
      }
      setStoredSelection(selection)
      setSelectionCallback?.(selection)
    },
    [entries, setSelectionCallback],
  )

  const anyDropdowns = entries.some((x) => !!x.dropdown) || true

  return (
    <div className={cx(headerTabsClass, className)}>
      <div className="_tabs-container">
        <div className="_tabs-rail _visible">
          <div className="_tabs">
            {entries.map((child, index) => {
              return (
                <div
                  key={child.key}
                  className={cx(
                    tabClass,
                    '_tab',
                    child.key === selection && '_selected',
                    child.pending && '_pending',
                    child.highlighted && '_highlighted',
                    child.tabWrappingClass,
                  )}
                  ref={(ref) => {
                    addRef(ref, index)
                  }}
                  onClick={() => setSelection(child.key)}
                >
                  {child.tab}
                </div>
              )
            })}
          </div>
        </div>

        <div className={cx('_tabs-rail', allVisible ? '_invisible' : '_visible')}>
          <div className="_tabs">
            {anyDropdowns && (
              <Dropdown
                arrowOffset={0}
                offset={{
                  mainAxis: 17,
                  crossAxis: 0,
                }}
                dropdownContent={entries.map((child) => (
                  <div
                    key={child.key}
                    className={cx(
                      '_dropdown-entry',
                      child.key === selection && '_selected',
                      child.pending && '_pending',
                      child.highlighted && '_highlighted',
                      child.dropdownWrappingClass,
                    )}
                    onClick={() => setSelection(child.key)}
                  >
                    {child.dropdown}
                  </div>
                ))}
                popupClassName={dropdownClass}
                open={open}
                onOpenChange={setOpen}
                placement="bottom-end"
                overridingPosition={chevronRef.current}
              />
            )}

            <div
              className={cx(
                tabClass,
                '_tab',
                firstEntry?.key === selection && '_selected',
                firstEntry?.pending && '_pending',
                firstEntry?.highlighted && '_highlighted',
                firstEntry?.tabWrappingClass,
              )}
            >
              {firstEntry?.tab}
              {anyDropdowns && (
                <Icon ref={chevronRef} iconUrl={ChevronIcon} size="xs" className={cx('_chevron', open && '_open')} onClick={() => setOpen(!open)} />
              )}
            </div>

            {alwaysVisible.map((child) => {
              return (
                <div
                  key={child.key}
                  className={cx(
                    tabClass,
                    '_tab',
                    child.key === selection && '_selected',
                    child.pending && '_pending',
                    child.highlighted && '_highlighted',
                    child.tabWrappingClass,
                  )}
                  onClick={() => setSelection(child.key)}
                >
                  {child.tab}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
